import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import DeviceCard from '../components/DeviceCard';
import { isDeviceOnline, formatDeviceTimestamp } from '../utils/timeUtils';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { isGlobalRole } from '../utils/roles';
import { getControllerType } from '../utils/controllerTypes';

function parseReadingsString(str) {
  const regex = /CO2:\s*(\d+)\s*ppm, Temp:\s*([\d.]+)\s*C, Hum:\s*([\d.]+)\s*%, VPD:\s*([\d.]+)\s*kPa\s*\(([^)]+)\), Light:\s*(\w+)/;
  const match = str.match(regex);
  if (!match) return null;
  const lightVal = match[6];
  return {
    co2: `${match[1]} ppm`,
    temperature: `${match[2]} °C`,
    humidity: `${match[3]} %`,
    vpd: `${match[4]} kPa (${match[5]})`,
    light: lightVal === 'Nighttime' || lightVal === 'Daylight' ? 0 : lightVal,
    lightStatus: lightVal === 'Nighttime' || lightVal === 'Daylight' ? lightVal : null,
  };
}

export default function ParticleDevicesPage() {
  const { t, lang } = useLang();
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  if (!token) return <Navigate to="/login" replace />;
  const [devices, setDevices] = useState([]);
  const [showOffline, setShowOffline] = useState(false);
  const [adminFarms, setAdminFarms] = useState([]);

  const handleUnauthorized = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await authFetch(
        '/api/devices/particle',
        {},
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const mapped = await Promise.all(
        list.map(async (d) => {
          let extra = {};
          try {
            const r = await fetch(`/api/sensor-data/climate/latest/${d.id}`);
            if (r.ok) {
              const latest = await r.json();
              if (latest) {
                extra = {
                  dayStatus: latest.dayStatus,
                  preset: latest.preset,
                  thresholds: latest.thresholds,
                  sockets: latest.sockets,
                  deviceStatuses: latest.deviceStatuses || {},
                  co2Warning: latest.co2Warning || null,
                };
              }
            }
          } catch (_) {}
          const toDisplay = (s) => (s ? s.replace(/_/g, ' ') : s);
          const controllerType = getControllerType(d);
          return {
            id: d.id,
            name: toDisplay(d.customName || d.name || d.id),
            farmId: d.farmId != null && d.farmId !== '' ? String(d.farmId) : '',
            controller: controllerType,
            controllerType,
            status: isDeviceOnline(d.lastUpdated) ? 'online' : 'offline',
            lastUpdated: d.lastUpdated,
            alertsEnabled: d.alertsEnabled !== false,

            temperature: d.readings?.temperature,
            humidity: d.readings?.humidity,
            co2: d.readings?.co2,
            vpd: d.readings?.vpd,
            lightStatus: d.readings?.lightStatus,
            co2Warning: d.co2Warning || extra.co2Warning || null,

                parsedReadings: {
              co2: d.readings?.co2 != null ? `${d.readings.co2} ppm` : null,
              temperature:
                d.readings?.temperature != null ? `${d.readings.temperature} °C` : null,
              humidity: d.readings?.humidity != null ? `${d.readings.humidity} %` : null,
              vpd: d.readings?.vpd != null ? `${d.readings.vpd} kPa` : null,
              light: d.readings?.light,
              lightStatus: d.readings?.lightStatus,
            },
            ...extra,
          };
        })
      );
      setDevices(mapped);
    } catch (err) {
      console.error('Failed to fetch devices', err);
    }
  }, [handleUnauthorized, token]);

  const refreshDevicesList = useCallback(async () => {
    try {
      const res = await authFetch(
        '/api/devices/refresh',
        { method: 'POST' },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) return;
      fetchDevices();
    } catch (err) {
      console.error('Failed to refresh devices list', err);
    }
  }, [fetchDevices, handleUnauthorized, token]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    if (!isGlobalRole(user?.role) || !token) {
      setAdminFarms([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/farms', {}, { token, onUnauthorized: handleUnauthorized });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setAdminFarms(Array.isArray(data) ? data : []);
      } catch (_) {
        if (!cancelled) setAdminFarms([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.role, token, handleUnauthorized]);

  const refreshDevice = async (id) => {
    try {
      const res = await authFetch(
        `/api/device/${id}/readings`,
        {},
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) return;
      const data = await res.json();
      const parsed = parseReadingsString(data.results?.Readings || '');
      let extra = {};
      try {
        const r = await fetch(`/api/sensor-data/climate/latest/${id}`);
        if (r.ok) {
          const latest = await r.json();
          if (latest) {
            extra = {
              dayStatus: latest.dayStatus,
              preset: latest.preset,
              thresholds: latest.thresholds,
              sockets: latest.sockets,

              temperature: latest.temperature,
              humidity: latest.humidity,
              co2: latest.co2,
              vpd: latest.vpd,
              lightStatus: latest.lightStatus,
              deviceStatuses: latest.deviceStatuses || {},
              co2Warning: latest.co2Warning || null,
            };
          }
        }
      } catch (_) {}
      setDevices((prev) =>
        prev.map((d) =>
          d.id === id
            ? {
                ...d,
                lastUpdated: new Date().toISOString(),
                status: 'online',
                temperature: extra.temperature,
                humidity: extra.humidity,
                co2: extra.co2,
                vpd: extra.vpd,
                lightStatus: extra.lightStatus,
                parsedReadings: parsed || d.parsedReadings,
                dayStatus: extra.dayStatus,
                preset: extra.preset,
                thresholds: extra.thresholds,
                sockets: extra.sockets,
                deviceStatuses: extra.deviceStatuses || d.deviceStatuses || {},
                co2Warning: extra.co2Warning || d.co2Warning || null,
              }
            : d
        )
      );
    } catch (err) {
      console.error('Failed to refresh device', err);
    }
  };

  const formatDate = (str) => {
    if (!str) return '-';
    const locale = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang;
    return formatDeviceTimestamp(str, locale);
  };

  const onlineDevices = devices.filter((d) => d.status !== 'offline');
  const offlineDevices = devices.filter((d) => d.status === 'offline');
  const canMoveDevicesAcrossFarms = isGlobalRole(user?.role);

  return (
    <div className="container mt-3">
      <div className="d-flex align-items-center mb-3">
        <h1 className="me-3">{t('nav.devices')}</h1>
        <button className="btn btn-secondary" onClick={refreshDevicesList}>
          {t('refresh')}
        </button>
      </div>
      <div className="row">
        {onlineDevices.map((d) => (
          <div key={d.id} className="col-12 col-md-6 col-lg-4 mb-3 shefa-device-card-slot">
            <DeviceCard
              device={d}
              t={t}
              lang={lang}
              formatDate={formatDate}
              onRefresh={refreshDevice}
              showFarmTransfer={canMoveDevicesAcrossFarms}
              allowFarmUnassign={isGlobalRole(user?.role)}
              farmsList={adminFarms}
              onDeviceFarmMoved={fetchDevices}
            />
          </div>
        ))}
      </div>
      {offlineDevices.length > 0 && (
        <div className="mt-3">
          <button className="btn btn-link" onClick={() => setShowOffline(!showOffline)}>
            {showOffline ? t('hideOffline') : t('showOffline', offlineDevices.length)}
          </button>
          {showOffline && (
            <div className="row mt-2">
              {offlineDevices.map((d) => (
                <div key={d.id} className="col-12 col-md-6 col-lg-4 mb-3 shefa-device-card-slot">
                  <DeviceCard
                    device={d}
                    t={t}
                    lang={lang}
                    formatDate={formatDate}
                    onRefresh={refreshDevice}
                    showFarmTransfer={canMoveDevicesAcrossFarms}
                    allowFarmUnassign={isGlobalRole(user?.role)}
                    farmsList={adminFarms}
                    onDeviceFarmMoved={fetchDevices}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
